import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { CrmQuotation, CrmQuotationJobType, CrmQuotationStatus, CrmQuotationType } from '../crm.model';
import { CrmService } from '../crm.service';

@Component({
  selector: 'app-quotation-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    BreadcrumbModule,
    ButtonModule,
    ConfirmDialogModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './quotation-list.component.html',
  styleUrls: ['./quotation-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class QuotationListComponent implements OnInit {
  private crmService = inject(CrmService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private router = inject(Router);

  quotations = signal<CrmQuotation[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  rows = signal(10);
  lastUpdated = signal<Date | null>(null);
  searchText = signal('');
  statusFilter = signal('');
  typeFilter = signal('');
  jobTypeFilter = signal('');
  deviationFilter = signal('');
  scorecards = signal<Record<string, any>>({});
  startFrom = signal<Date | null>(null);
  startTo = signal<Date | null>(null);
  amountFrom = signal<number | null>(null);
  amountTo = signal<number | null>(null);
  lastLazyEvent: TableLazyLoadEvent | null = null;

  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
  breadcrumbItems: MenuItem[] = [
    { label: 'CRM', routerLink: '/crm/quotations' },
    { label: 'Quotations' }
  ];

  statuses = Object.values(CrmQuotationStatus).map(value => ({ label: value, value }));
  types = Object.values(CrmQuotationType).map(value => ({ label: value, value }));
  jobTypes = Object.values(CrmQuotationJobType).map(value => ({ label: value.replaceAll('_', ' '), value }));
  deviationStatuses = ['NO_CRITERIA', 'WITHIN_CRITERIA', 'DEVIATION'].map(value => ({ label: value.replaceAll('_', ' '), value }));

  activeSearchCount = computed(() => [
    this.searchText(),
    this.statusFilter(),
    this.typeFilter(),
    this.jobTypeFilter(),
    this.deviationFilter(),
    this.startFrom(),
    this.startTo(),
    this.amountFrom(),
    this.amountTo()
  ].filter(value => value !== null && value !== '').length);

  metrics = computed(() => {
    const cards = this.scorecards();
    return [
      { label: 'Total Quotes', value: Number(cards['total'] || 0) },
      { label: 'Preparing', value: Number(cards['preparing'] || 0) },
      { label: 'Approved', value: Number(cards['approved'] || 0) },
      { label: 'Approval Required', value: Number(cards['approvalRequired'] || 0) },
      { label: 'Pipeline Value', value: Number(cards['pipelineValue'] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
    ];
  });

  get lastUpdatedText() {
    return this.lastUpdated() ? `Updated ${this.lastUpdated()!.toLocaleTimeString()}` : 'Not loaded';
  }

  ngOnInit() {
    this.loadScorecards();
    this.loadQuotations();
  }

  loadScorecards() {
    this.crmService.getQuotationScorecards().subscribe(cards => this.scorecards.set(cards || {}));
  }

  loadQuotations(event?: TableLazyLoadEvent) {
    if (event) this.lastLazyEvent = event;
    const lazy = event || this.lastLazyEvent;
    const page = lazy?.first && lazy.rows ? Math.floor(lazy.first / lazy.rows) : 0;
    const size = lazy?.rows || this.rows();
    const sortBy = Array.isArray(lazy?.sortField) ? lazy?.sortField[0] : lazy?.sortField || 'createdAt';
    const sortDir = lazy?.sortOrder === 1 ? 'asc' : 'desc';
    this.rows.set(size);
    this.loading.set(true);
    this.crmService.searchQuotations({
      searchText: this.searchText(),
      status: this.statusFilter(),
      type: this.typeFilter(),
      jobType: this.jobTypeFilter(),
      deviationStatus: this.deviationFilter(),
      startFrom: this.startFrom() || undefined,
      startTo: this.startTo() || undefined,
      amountFrom: this.amountFrom() ?? undefined,
      amountTo: this.amountTo() ?? undefined,
      page,
      size,
      sortBy,
      sortDir
    }).subscribe({
      next: pageResponse => {
        this.quotations.set(pageResponse.content || []);
        this.totalRecords.set(pageResponse.totalElements || 0);
        this.lastUpdated.set(new Date());
        this.loading.set(false);
        this.loadScorecards();
      },
      error: () => this.loading.set(false)
    });
  }

  applySearch() {
    this.loadQuotations({ first: 0, rows: this.rows() });
  }

  clearSearch() {
    this.searchText.set('');
    this.statusFilter.set('');
    this.typeFilter.set('');
    this.jobTypeFilter.set('');
    this.deviationFilter.set('');
    this.startFrom.set(null);
    this.startTo.set(null);
    this.amountFrom.set(null);
    this.amountTo.set(null);
    this.applySearch();
  }

  openNew() {
    this.router.navigate(['/crm/quotations/new']);
  }

  openTermsConfig() {
    this.router.navigate(['/crm/quotations/terms']);
  }

  openWorkflowConfig() {
    this.router.navigate(['/workflow/config'], { queryParams: { resourceKey: 'CRM_QUOTATION' } });
  }

  editQuotation(quotation: CrmQuotation) {
    this.router.navigate(['/crm/quotations', quotation.id, 'edit']);
  }

  canCreateSalesOrder(quotation: CrmQuotation) {
    return ['APPROVED', 'JOB', 'COMPLETED'].includes(quotation.status);
  }

  createSalesOrder(quotation: CrmQuotation) {
    if (!quotation.id) return;
    this.router.navigate(['/crm/quotations', quotation.id, 'sales-order-conversion']);
  }

  deleteQuotation(quotation: CrmQuotation) {
    if (!quotation.id) return;
    this.confirmationService.confirm({
      message: `Delete quotation <b>${quotation.quotationNumber || quotation.subject}</b>?`,
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.crmService.deleteQuotation(quotation.id!).subscribe(() => {
          this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Quotation deleted.' });
          this.loadQuotations();
        });
      }
    });
  }

  getSeverity(status?: string) {
    switch (status) {
      case 'COMPLETED':
      case 'APPROVED':
      case 'JOB':
        return 'success';
      case 'SUBMITTED':
      case 'CLIENT_REVIEW':
      case 'READY_TO_SUBMIT':
        return 'info';
      case 'PREPARING':
      case 'INTERNAL_REVIEW':
      case 'REVISION_REQUESTED':
        return 'warn';
      case 'REJECTED':
        return 'danger';
      default:
        return 'secondary';
    }
  }
}
